import type { CSSProperties } from '@emotion/serialize';
import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';

import { Callout } from './Callout';
import { useOnClickTempToggle } from './use-on-click-temp-toggle';

/**
 * @deprecated Use {@link HubTooltip} instead.
 */
export interface UseCalloutOptions {
	text: ReactNode;
	position?: 'center' | 'left';
	horizontal?: 'left' | 'right';
	maxWidth?: CSSProperties['maxWidth'];
	tooltip?: boolean;
}

/**
 * A wrapper around the Callout component that handles the basic use case where
 * you click on an element and show the callout until the mouse moves away from
 * the element.
 *
 * More complex use cases should use the constituent parts instead of adding
 * complexity to this hook.
 *
 * @deprecated Use {@link HubTooltip} instead.
 */
export function useCallout({
	text,
	position,
	horizontal,
	tooltip,
	maxWidth,
}: UseCalloutOptions) {
	const calloutRef = useRef<HTMLElement>(null);
	const [showingCallout, setShowingCallout] = useState(false);
	const setShowViewCallout = useOnClickTempToggle({
		ref: calloutRef,
		callback: useCallback(() => setShowingCallout(true), []),
		offCallback: useCallback(() => setShowingCallout(false), []),
	});

	const callout = showingCallout ? (
		<Callout
			targetRef={calloutRef}
			position={position}
			horizontal={horizontal}
			css={{
				maxWidth,

				// Undo the transform in global-styles.tsx
				// because this needs to display in front of portaled elements in Safari
				transform: 'translateZ(1000px)',
			}}
			tooltip={tooltip}
		>
			{text}
		</Callout>
	) : null;

	return { callout, setShowViewCallout } as const;
}
